import { useEffect, useState } from "react";
import { Box, Button, Flex, SimpleGrid, Text, useToast } from "@chakra-ui/react";
import { crm } from "../../../theme";
import {
  getRegistrationDefaults,
  RegDefaultsModel,
  RegDefaultsView,
  saveRegistrationDefaults,
} from "../../../services/govRegistrationService";
import { DocumentsEditor, ItemSettingsFields, TextInput } from "../../process/components/RegistrationFields";

const title = { fontSize: "13px", fontWeight: "700", color: crm.text, mb: "8px", mt: "20px" };

/** Padrões globais do registro DUIMP. Admin edita; demais leem. Campo vazio cai no appsettings (Siscomex:*). */
const RegistrationDefaults = () => {
  const toast = useToast();
  const [view, setView] = useState<RegDefaultsView | null>(null);
  const [form, setForm] = useState<RegDefaultsModel | null>(null);
  const [saving, setSaving] = useState(false);

  const apply = (v: RegDefaultsView) => {
    setView(v);
    setForm({ ...v.values, itemDefaults: v.values.itemDefaults ?? {}, documents: v.values.documents ?? [] });
  };

  useEffect(() => {
    getRegistrationDefaults()
      .then(apply)
      .catch(() => toast({ title: "Não foi possível carregar os padrões do registro", status: "error" }));
  }, []);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      apply(await saveRegistrationDefaults(form));
      toast({ title: "Padrões salvos", status: "success" });
    } catch (e: any) {
      toast({ title: "Não foi possível salvar", description: e.message, status: "error", duration: 10000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const set = (patch: Partial<RegDefaultsModel>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const fb = view?.configFallback;

  return (
    <Box bg={crm.surface} border={`1px solid ${crm.border}`} borderRadius={crm.radius} boxShadow={crm.shadowSm} p="24px">
      <Text fontSize="18px" fontWeight="700" color={crm.text}>
        Registro DUIMP
      </Text>
      <Text fontSize="13px" color={crm.textMuted}>
        Valores padrão que pré-preenchem "Dados da DUIMP" em cada processo. Campo vazio usa a configuração do servidor.
      </Text>
      {form && view && (
        <>
          <Text {...title}>Despacho e local de consumo</Text>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px">
            <TextInput label="Unidade RFB de despacho (7 dígitos)" value={form.declaredUnitCode} maxLength={7} placeholder={fb?.declaredUnitCode ? `Config: ${fb.declaredUnitCode}` : "ex.: 0817800"} onChange={(v) => set({ declaredUnitCode: v })} />
            <TextInput label="UF do local de consumo" value={form.consumptionUf} maxLength={2} placeholder={fb?.consumptionUf ? `Config: ${fb.consumptionUf}` : "vazio: busca pelo CNPJ do importador"} onChange={(v) => set({ consumptionUf: v?.toUpperCase() ?? null })} />
            <TextInput label="Município IBGE (7 dígitos)" value={form.consumptionCityIbge} maxLength={7} placeholder={fb?.consumptionCityIbge ? `Config: ${fb.consumptionCityIbge}` : ""} onChange={(v) => set({ consumptionCityIbge: v })} />
          </SimpleGrid>

          <Text {...title}>Documentos de instrução</Text>
          <DocumentsEditor
            value={form.documents ?? []}
            onChange={(d) => set({ documents: d })}
            hint={
              fb?.documents?.length
                ? "Lista vazia usa os documentos da configuração do servidor. Valores aceitam {invoice}, {bl} e {ce}."
                : "Valores aceitam {invoice}, {bl} e {ce} (preenchidos com os dados do processo)."
            }
          />

          <Text {...title}>Condições dos itens</Text>
          <ItemSettingsFields value={form.itemDefaults} domains={view.domains} onChange={(v) => set({ itemDefaults: v })} />

          <Flex justify="flex-end" mt="24px">
            <Button colorScheme="blue" onClick={save} isLoading={saving}>
              Salvar padrões
            </Button>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default RegistrationDefaults;
